import AppLayout from 'components/layout/app-shell/AppLayout';
import PublicLayout from 'components/layout/public/PublicLayout';
import AuthLayout from 'components/layout/public/AuthLayout';
import BaseErrorBoundaryHandler from 'components/core/base-error-boundary-handler';
import { JSX, lazy, Suspense } from 'react';
import { createRoutesFromElements, Navigate, Route, createBrowserRouter } from 'react-router-dom';
import ProtectedRoute from './protected-route';
import { ROUTES } from '../../config/route-configuration';
import LoadingIndicator from 'components/ui/loading/LoadingIndicator';

const RootRedirect = lazy(() => import('./root-redirect'));
const ClientsPage = lazy(() => import('./clients/clients-page'));
const SignUpPage = lazy(() => import('./account-management/sign-up-page'));
const LoginPage = lazy(() => import('./account-management/login-page'));
const ForgotPasswordPage = lazy(() => import('./account-management/forgot-password-page'));
const PublicProposalPage = lazy(() => import('./public/public-proposal-page'));
const VisitorGeneratePage = lazy(() => import('./public/visitor-generate-page'));
const VisitorProposalPage = lazy(() => import('./public/visitor-proposal-page'));
const DashboardPage = lazy(() => import('./dashboard/dashboard-page'));
const TemplatesPage = lazy(() => import('./templates/templates-page'));
const AccountSettingsPage = lazy(() => import('./account/account-settings-page'));
const ProposalEditorPage = lazy(() => import('./proposal-editor/proposal-editor-page'));
const ProposalPage = lazy(() => import('./proposals/proposal-page'));
const ProposalTimelinePage = lazy(() => import('./proposals/proposal-timeline-page'));
const ProposalsDraftsPage = lazy(() => import('./proposals/proposals-drafts-page'));
const ProposalsSentPage = lazy(() => import('./proposals/proposals-sent-page'));
const ProposalsSignedPage = lazy(() => import('./proposals/proposals-signed-page'));
const ProposalsIndexPage = lazy(() => import('./proposals/proposals-index-page'));
const UnauthorizedPage = lazy(() => import('./unauthorized/unauthorized-page'));
const ClientDetailPage = lazy(() => import('./clients/client-detail-page'));

const withSuspense = (element: JSX.Element) => (
  <Suspense
    fallback={
      <div className="flex min-h-[40vh] items-center justify-center">
        <LoadingIndicator />
      </div>
    }
  >
    {element}
  </Suspense>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      {/* ===================== */}
      {/* Public routes */}
      {/* ===================== */}
      <Route element={<PublicLayout />} errorElement={<BaseErrorBoundaryHandler />}>
        <Route path={ROUTES.HOME} element={withSuspense(<RootRedirect />)} />
        <Route path={ROUTES.GENERATE} element={withSuspense(<VisitorGeneratePage />)} />

        <Route path={ROUTES.FORGOT_PASSWORD} element={withSuspense(<ForgotPasswordPage />)} />
        <Route path={ROUTES.UNAUTHORIZED} element={withSuspense(<UnauthorizedPage />)} />
      </Route>

      <Route element={<PublicLayout hideHeader />} errorElement={<BaseErrorBoundaryHandler />}>
        <Route path={ROUTES.PUBLIC_PROPOSAL} element={withSuspense(<PublicProposalPage />)} />
        <Route path={ROUTES.VISITOR_PROPOSAL_PREVIEW} element={withSuspense(<VisitorProposalPage />)} />
      </Route>

      <Route element={<AuthLayout />} errorElement={<BaseErrorBoundaryHandler />}>
        <Route path={ROUTES.LOGIN} element={withSuspense(<LoginPage />)} />
        <Route path={ROUTES.SIGNUP} element={withSuspense(<SignUpPage />)} />
      </Route>

      {/* ===================== */}
      {/* Authenticated app */}
      {/* ===================== */}
      <Route element={<AppLayout />} errorElement={<BaseErrorBoundaryHandler />}>
        <Route
          path={ROUTES.DASHBOARD}
          element={withSuspense(
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          )}
        />

        <Route
          path={ROUTES.CLIENTS}
          element={withSuspense(
            <ProtectedRoute>
              <ClientsPage />
            </ProtectedRoute>
          )}
        />
        <Route
          path={ROUTES.CLIENT_DETAIL}
          element={withSuspense(
            <ProtectedRoute>
              <ClientDetailPage />
            </ProtectedRoute>
          )}
        />

        <Route
          path={ROUTES.ACCOUNT_SETTINGS}
          element={withSuspense(
            <ProtectedRoute>
              <AccountSettingsPage />
            </ProtectedRoute>
          )}
        />
        <Route
          path={ROUTES.TEMPLATES}
          element={withSuspense(
            <ProtectedRoute>
              <TemplatesPage />
            </ProtectedRoute>
          )}
        />

        <Route
          path={ROUTES.PROPOSAL_EDITOR}
          element={withSuspense(
            <ProtectedRoute>
              <ProposalEditorPage />
            </ProtectedRoute>
          )}
        />

        <Route
          path={ROUTES.PROPOSAL_EDIT}
          element={withSuspense(
            <ProtectedRoute>
              <ProposalPage mode="edit" />
            </ProtectedRoute>
          )}
        />
        <Route
          path={ROUTES.PROPOSAL_PREVIEW}
          element={withSuspense(
            <ProtectedRoute>
              <ProposalPage mode="preview" />
            </ProtectedRoute>
          )}
        />
        <Route
          path={ROUTES.PROPOSAL_TIMELINE}
          element={withSuspense(
            <ProtectedRoute>
              <ProposalTimelinePage />
            </ProtectedRoute>
          )}
        />
        <Route
          path={ROUTES.PROPOSALS_INDEX}
          element={withSuspense(
            <ProtectedRoute>
              <ProposalsIndexPage />
            </ProtectedRoute>
          )}
        />
        <Route
          path={ROUTES.PROPOSALS_DRAFTS}
          element={withSuspense(
            <ProtectedRoute>
              <ProposalsDraftsPage />
            </ProtectedRoute>
          )}
        />
        <Route
          path={ROUTES.PROPOSALS_SENT}
          element={withSuspense(
            <ProtectedRoute>
              <ProposalsSentPage />
            </ProtectedRoute>
          )}
        />
        <Route
          path={ROUTES.PROPOSALS_SIGNED}
          element={withSuspense(
            <ProtectedRoute>
              <ProposalsSignedPage />
            </ProtectedRoute>
          )}
        />
        <Route
          path={ROUTES.ACCOUNT_USERS}
          element={
            <ProtectedRoute>
              <Navigate to={ROUTES.DASHBOARD} replace />
            </ProtectedRoute>
          }
        />
      </Route>
    </>
  )
);

export default router;
